/**
 * Admin API Client
 * Functions for calling admin backend endpoints
 */

import { supabase, currentSupabaseEnv } from './supabase';
import type { Unit, Group, IndustryType } from '@/types';
import type { IndustryLabel } from '@/contexts/SettingsContext';

/**
 * API Base URL - Uses relative path for same-origin requests
 *
 * This approach is bulletproof because:
 * - Relative `/api` path works on ALL domains (parent group, demo, localhost)
 * - No dependency on environment variables being set correctly
 * - No hardcoded domain URLs that could become stale
 */
// Exported so documentsApi can do its own streamed fetch (a ZIP body cannot go
// through adminFetch's JSON path) without re-implementing auth.
export const API_BASE_URL = '/api';

/**
 * Get authorization header with Supabase token
 * All requests require authentication - no demo mode bypass
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('User not authenticated. Please log in.');
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
    'X-Supabase-Environment': currentSupabaseEnv,
  };
}

/**
 * Generic fetch wrapper with auth
 * Backend uses hostname-based routing to select correct Supabase instance
 */
export async function adminFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeaders();
  const url = `${API_BASE_URL}/v1/admin${endpoint}`;

  console.log(`[adminApi] ${options.method || 'GET'} ${url}`);

  const merged: Record<string, string> = {
    ...(headers as Record<string, string>),
    ...(options.headers as Record<string, string>),
  };
  if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
    delete merged['Content-Type'];
  }

  const response = await fetch(url, {
    ...options,
    headers: merged,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    // 401 = invalid/expired token - clear stale session so user is prompted to re-login
    if (response.status === 401) {
      // 'local': only this browser's stale session, never Pocket's or Sales' (T-152).
      await supabase.auth.signOut({ scope: 'local' });
    }
    throw new Error(error.message || `API Error: ${response.status}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// =====================================================
// ME
// =====================================================

export async function getAdminMe() {
  return adminFetch<{
    user: {
      id: string;
      email: string;
      first_name?: string;
      last_name?: string;
      is_internal: boolean;
      default_company_id?: string;
      default_group_id?: string;
    };
    companies: Array<{
      id: string;
      name: string;
      slug: string;
      is_internal: boolean;
      industry?: string;
      role: string;
    }>;
    memberships: Array<{
      id: string;
      company_id: string;
      group_id: string | null;
      role: string;
      company_name: string;
      group_name?: string;
    }>;
    permissions: string[];
    // T-068 P3: coarse feature-visibility flags computed server-side (e.g.
    // reportAdminScope). The API remains the enforcement point.
    capabilities?: { report_subscriptions_admin?: boolean; unbilled_deployments_view?: boolean };
    is_superadmin: boolean;
  }>('/me');
}

// =====================================================
// SHARED RESPONSE SHAPES
// =====================================================
// These mirror the rows the admin endpoints return, scoped to the fields the
// app actually consumes. Companies/users have no shared domain type, so the
// response shape is declared here; units/groups reuse the canonical @/types.

/** Company row as returned by the admin companies endpoints. */
interface AdminCompany {
  id: string;
  name: string;
  slug: string;
  is_internal: boolean;
  industry?: string;
  created_at: string;
}

/** User row as returned by the admin users endpoints. */
interface AdminUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  is_internal: boolean;
  wearables_enabled?: boolean;
  created_at: string;
  default_company_id?: string;
  default_group_id?: string;
  memberships: Array<{
    id: string;
    company_id: string;
    company_name: string;
    group_id?: string;
    group_name?: string;
    role: string;
  }>;
}

// =====================================================
// COMPANIES
// =====================================================

export async function getAdminCompanys(params?: {
  industries?: string[];
}) {
  const queryParams = new URLSearchParams();
  if (params?.industries && params.industries.length > 0) {
    params.industries.forEach(industry => queryParams.append('industries', industry));
  }
  const query = queryParams.toString();
  return adminFetch<{ companies: AdminCompany[] }>(`/companies${query ? `?${query}` : ''}`);
}

export async function getAdminCompany(id: string) {
  return adminFetch<{ company: AdminCompany }>(`/companies/${id}`);
}

export async function createAdminCompany(data: unknown) {
  return adminFetch<{ company: AdminCompany }>('/companies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminCompany(id: string, data: unknown) {
  return adminFetch<{ company: AdminCompany }>(`/companies/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminCompany(id: string) {
  return adminFetch<{}>(`/companies/${id}`, {
    method: 'DELETE',
  });
}

// =====================================================
// USERS
// =====================================================

export async function getAdminUsers(params?: {
  companyId?: string;
  groupIds?: string[];
  industries?: string[];
}) {
  const queryParams = new URLSearchParams();
  if (params?.companyId) queryParams.append('companyId', params.companyId);
  if (params?.groupIds && params.groupIds.length > 0) {
    // Filter out synthetic __unassigned_* IDs - they're not valid UUIDs
    const validGroupIds = params.groupIds.filter(id => !id.startsWith('__unassigned'));
    validGroupIds.forEach(id => queryParams.append('groupIds', id));
  }
  if (params?.industries && params.industries.length > 0) {
    params.industries.forEach(industry => queryParams.append('industries', industry));
  }
  const query = queryParams.toString();
  return adminFetch<{ users: AdminUser[] }>(`/users${query ? `?${query}` : ''}`);
}

export async function getAdminUser(id: string) {
  return adminFetch<{ user: AdminUser }>(`/users/${id}`);
}

export async function createAdminUser(data: unknown) {
  return adminFetch<{ user: AdminUser }>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminUser(id: string, data: unknown) {
  return adminFetch<{ message: string }>(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminUser(id: string) {
  return adminFetch<{}>(`/users/${id}`, {
    method: 'DELETE',
  });
}

export async function resetAdminUserPassword(id: string, password: string) {
  return adminFetch<{ success: boolean }>(`/users/${id}/password-reset`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

// =====================================================
// GROUPS
// =====================================================

export async function getAdminGroups(params?: {
  companyId?: string;
  groupIds?: string[];
  industries?: string[];
}) {
  const queryParams = new URLSearchParams();
  if (params?.companyId) queryParams.append('companyId', params.companyId);
  if (params?.groupIds && params.groupIds.length > 0) {
    // Filter out synthetic __unassigned_* IDs - they're not valid UUIDs
    const validGroupIds = params.groupIds.filter(id => !id.startsWith('__unassigned'));
    validGroupIds.forEach(id => queryParams.append('groupIds', id));
  }
  if (params?.industries && params.industries.length > 0) {
    params.industries.forEach(industry => queryParams.append('industries', industry));
  }
  const query = queryParams.toString();
  return adminFetch<{ groups: Group[] }>(`/groups${query ? `?${query}` : ''}`);
}

export async function getAdminGroup(id: string) {
  return adminFetch<{ group: Group }>(`/groups/${id}`);
}

export async function createAdminGroup(data: unknown) {
  return adminFetch<{ group: Group }>('/groups', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminGroup(id: string, data: unknown) {
  return adminFetch<{ group: Group }>(`/groups/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminGroup(id: string) {
  return adminFetch<{}>(`/groups/${id}`, {
    method: 'DELETE',
  });
}

// =====================================================
// UNITS
// =====================================================

/**
 * Per-unit system status the admin units route builds from linked devices
 * (mirrors the map-sidebar display). This is the API boundary's own shape —
 * looser and flatter than the fleet-map `Unit` type — so it lives here rather
 * than being re-declared by each consuming page (ISS-031).
 */
export interface AdminUnitSystems {
  starlink?: {
    enabled: boolean;
    connectionStatus: 'online' | 'offline' | 'idle' | 'suspended' | 'degraded' | 'unknown' | 'not_activated';
    deviceId: string | null;
    externalId: string | null;
  };
  solar?: {
    enabled: boolean;
    generation: number;
    battery: number;
    isCharging: boolean;
    deviceId: string | null;
    externalId: string | null;
    name: string | null;
    physicalInverterCount?: number;
  };
  charger?: {
    enabled: boolean;
    status: string;
    deviceId: string | null;
    externalId: string | null;
  };
  hvac?: {
    enabled: boolean;
    mode: string;
    deviceId: string | null;
    externalId: string | null;
  };
  omni?: {
    enabled: boolean;
    deviceId: string | null;
    externalId: string | null;
  };
}

/**
 * Raw unit row as returned by `GET /admin/units`: the raw `units` columns plus
 * the renamed `company` join, `primary_group` join, and the built `systems`
 * object. Deliberately looser than the fleet-map `Unit` (`@/types`) so the
 * settings page can consume it without a double assertion (ISS-031).
 */
export interface AdminUnitRow {
  id: string;
  // unit_code and company_id are NOT NULL columns, so the route always returns
  // them; kept required so consumers don't re-null-check what can't be null.
  unit_code: string;
  // vin and industry_type are nullable columns: the raw row carries null (not
  // just absent) when unset, so model null explicitly (ISS-031, CodeRabbit).
  vin?: string | null;
  status?: 'active' | 'inactive' | 'maintenance' | 'off_rent';
  model_code?: string;
  model?: string;
  industry_type?: IndustryType | null;
  company_id: string;
  primary_group_id?: string;
  primary_group?: { name?: string } | null;
  companies?: { name?: string } | null;
  company?: { name?: string } | null;
  last_seen_at?: string;
  lastUpdate?: string;
  updated_at?: string;
  name?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  state_province?: string;
  country?: string;
  systems?: AdminUnitSystems;
  first_seen_at?: string;
  created_at?: string;
  last_comms_at?: string;
}

export async function getAdminUnits(params?: {
  companyId?: string;
  groupIds?: string[];
  statuses?: string[];
  models?: string[];
  industries?: string[];
}) {
  const queryParams = new URLSearchParams();
  if (params?.companyId) queryParams.append('companyId', params.companyId);
  if (params?.groupIds && params.groupIds.length > 0) {
    // Filter out synthetic __unassigned_* IDs - they're not valid UUIDs
    const validGroupIds = params.groupIds.filter(id => !id.startsWith('__unassigned'));
    validGroupIds.forEach(id => queryParams.append('groupIds', id));
  }
  if (params?.statuses && params.statuses.length > 0) {
    params.statuses.forEach(status => queryParams.append('statuses', status));
  }
  if (params?.models && params.models.length > 0) {
    params.models.forEach(model => queryParams.append('models', model));
  }
  if (params?.industries && params.industries.length > 0) {
    params.industries.forEach(industry => queryParams.append('industries', industry));
  }
  const query = queryParams.toString();
  return adminFetch<{ units: AdminUnitRow[] }>(`/units${query ? `?${query}` : ''}`);
}

export interface UnitModelMoveResult {
  unit: { id: string; unit_code: string; model_code: string };
  old_unit_code: string;
  new_unit_code: string;
  old_model_code: string;
  new_model_code: string;
  qr_reprint_required: boolean;
}

// ISS-037: re-home a unit to another model. Regenerates its unit_code in place
// (same UUID); guarded server-side against active holds/rentals + retired models.
export async function moveUnitModel(id: string, targetModelCode: string): Promise<UnitModelMoveResult> {
  return adminFetch<UnitModelMoveResult>(`/units/${id}/move-model`, {
    method: 'POST',
    body: JSON.stringify({ target_model_code: targetModelCode }),
  });
}

export interface BulkUnitModelMoveResult {
  from_model: string;
  to_model: string;
  total: number;
  moved: Array<{ id: string; old_unit_code: string; new_unit_code: string }>;
  skipped: Array<{ id: string; unit_code: string; code: string; reason: string }>;
  failed: Array<{ id: string; unit_code: string; code: string; reason: string }>;
  qr_reprint_required: boolean;
}

// ISS-037 bulk: move ALL units from one model to another. Units on an active
// rental/hold are skipped + reported (not failed).
export async function bulkMoveUnitsToModel(fromModelCode: string, toModelCode: string): Promise<BulkUnitModelMoveResult> {
  return adminFetch<BulkUnitModelMoveResult>('/units/bulk-move-model', {
    method: 'POST',
    body: JSON.stringify({ from_model_code: fromModelCode, to_model_code: toModelCode }),
  });
}

export async function getAdminUnit(id: string) {
  return adminFetch<{ unit: Unit }>(`/units/${id}`);
}

/** One system's auto-link outcome on unit create (ISS-046). Additive, open strings. */
export interface AutoLinkOutcome {
  linked: boolean;
  reason?: 'no_match' | 'ambiguous' | 'conflict' | 'raced' | 'error' | string;
  candidates?: number;
  plant_id?: string;
  terminal_id?: string;
}

/** `auto_link` on the create-unit 201: which already-discovered hardware came along. */
export interface AutoLinkResult {
  solark?: AutoLinkOutcome;
  starlink?: AutoLinkOutcome;
  new_devices?: { resolved: boolean; reason?: string };
}

export async function createAdminUnit(data: unknown) {
  // `auto_link` is null when auto-link could not run; absent on an older API.
  return adminFetch<{ unit: Unit; auto_link?: AutoLinkResult | null }>('/units', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminUnit(id: string, data: unknown) {
  return adminFetch<{ unit: Unit }>(`/units/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminUnit(id: string) {
  return adminFetch<{}>(`/units/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Check if a unit code exists globally (bypasses RBAC for uniqueness check)
 * Returns only boolean to prevent data leakage
 */
export async function checkUnitCodeExists(
  unitCode: string,
  excludeUnitId?: string
): Promise<boolean> {
  const params = new URLSearchParams();
  if (excludeUnitId) {
    params.set('excludeUnitId', excludeUnitId);
  }

  const queryString = params.toString();
  const endpoint = `/units/check-code/${encodeURIComponent(unitCode.trim())}${queryString ? `?${queryString}` : ''}`;

  const response = await adminFetch<{ exists: boolean }>(endpoint);
  return response.exists;
}

// =====================================================
// MEMBERSHIPS
// =====================================================

export async function createMembership(data: {
  user_id: string;
  company_id: string;
  group_id?: string;
  role: string;
}) {
  return adminFetch<{ membership: Record<string, unknown> }>('/memberships', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateMembership(id: string, data: { role: string }) {
  return adminFetch<{ membership: Record<string, unknown> }>(`/memberships/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteMembership(id: string) {
  return adminFetch<{}>(`/memberships/${id}`, {
    method: 'DELETE',
  });
}

// =====================================================
// INVITATIONS (Authenticated - Admin endpoints)
// =====================================================

export interface InvitationGroup {
  group_id: string;
  group_name: string;
}

export interface Invitation {
  id: string;
  email: string;
  company_id: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  invited_by: string;
  created_at: string;
  expires_at: string;
  accepted_at?: string;
  full_name?: string;
  phone?: string;
  message?: string;
  resend_count: number;
  resent_at?: string;
  groups: InvitationGroup[];
  // Joined fields
  company_name?: string;
  inviter_name?: string;
  inviter_email?: string;
}

export async function getAdminInvitations(params?: {
  companyId?: string;
  status?: string;
  industries?: string[];
}) {
  const queryParams = new URLSearchParams();
  if (params?.companyId) queryParams.append('companyId', params.companyId);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.industries && params.industries.length > 0) {
    params.industries.forEach(industry => queryParams.append('industries', industry));
  }
  const query = queryParams.toString();
  return adminFetch<{ invitations: Invitation[] }>(`/invitations${query ? `?${query}` : ''}`);
}

export interface CreateInvitationData {
  email: string;
  company_id: string;
  group_ids?: string[];  // Multi-group support
  role: string;
  full_name?: string;
  phone?: string;
  message?: string;  // Personal message from inviter
}

export interface CreateInvitationResponse {
  invitation: Invitation;
  invite_url: string;
  verification_code: string;  // 4-digit code to share with invitee
  /** Actual email outcome: 'sent' = delivered to the mail provider; 'route_disabled'
   *  = the invitations email route is off; 'failed' = the send errored. In the last
   *  two, no email went out — share the URL + code manually. */
  email_delivery?: 'sent' | 'route_disabled' | 'failed';
  message: string;
}

export async function createInvitation(data: CreateInvitationData) {
  return adminFetch<CreateInvitationResponse>('/invitations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface ResendInvitationResponse {
  invitation: Invitation;
  invite_url: string;
  verification_code: string;  // New 4-digit code
  email_delivery?: 'sent' | 'route_disabled' | 'failed';
  message: string;
}

export async function resendInvitation(id: string) {
  return adminFetch<ResendInvitationResponse>(`/invitations/${id}/resend`, {
    method: 'POST',
  });
}

export async function revokeInvitation(id: string) {
  return adminFetch<{ invitation: Invitation }>(`/invitations/${id}/revoke`, {
    method: 'PATCH',
  });
}

export async function deleteInvitation(id: string) {
  return adminFetch<{}>(`/invitations/${id}`, {
    method: 'DELETE',
  });
}

// =====================================================
// FILTERS
// =====================================================

export async function getFilterIndustries(): Promise<{ industries: FilterIndustry[] }> {
  return adminFetch<{ industries: FilterIndustry[] }>('/filters/industries');
}

export async function createFilterIndustry(data: { name: string; slug: string }): Promise<{ industry: FilterIndustry }> {
  return adminFetch<{ industry: FilterIndustry }>('/filters/industries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// slug is locked after creation (companies.industry resolves by it) — edit the
// display name and ordering only.
export async function updateFilterIndustry(id: string, data: { name?: string; display_order?: number; is_active?: boolean }): Promise<{ industry: FilterIndustry }> {
  return adminFetch<{ industry: FilterIndustry }>(`/filters/industries/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteFilterIndustry(id: string): Promise<Record<string, never>> {
  return adminFetch<Record<string, never>>(`/filters/industries/${id}`, {
    method: 'DELETE',
  });
}

export async function getFilterModels(): Promise<{ models: FilterModel[] }> {
  return adminFetch<{ models: FilterModel[] }>('/filters/models');
}

export async function createFilterModel(data: { code: string; label: string; callsigns?: string[] }): Promise<{ model: FilterModel }> {
  return adminFetch<{ model: FilterModel }>('/filters/models', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// code is append-only (units + Sales/Landing pricing resolve by it) — edit the
// display label and ordering only. is_active drives retire/reactivate.
export async function updateFilterModel(id: string, data: { label?: string; display_order?: number; is_active?: boolean; callsigns?: string[] }): Promise<{ model: FilterModel }> {
  return adminFetch<{ model: FilterModel }>(`/filters/models/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteFilterModel(id: string): Promise<Record<string, never>> {
  return adminFetch<Record<string, never>>(`/filters/models/${id}`, {
    method: 'DELETE',
  });
}

// =====================================================
// LABELS
// =====================================================

export async function getAdminLabels() {
  return adminFetch<{ labels: IndustryLabel[] }>('/labels');
}

export async function createAdminLabel(data: {
  industry: string;
  label_type: 'group' | 'unit' | 'site' | 'team';
  label_value: string;
  icon?: string | null;
  superadmin_override?: boolean;
  admin_override?: boolean;
}) {
  return adminFetch<{ label: IndustryLabel }>('/labels', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminLabel(id: string, data: {
  industry?: string;
  label_type?: 'group' | 'unit' | 'site' | 'team';
  label_value?: string;
  icon?: string | null;
  superadmin_override?: boolean;
  admin_override?: boolean;
}) {
  return adminFetch<{ label: IndustryLabel }>(`/labels/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminLabel(id: string) {
  return adminFetch<{}>(`/labels/${id}`, {
    method: 'DELETE',
  });
}

// =====================================================
// AUDIT LOGS
// =====================================================

import type { AuditLog, AuditLogSubItem, Invoice, SystemNotification, FilterIndustry, FilterModel } from '@/types';

export async function getAuditLogs(params?: {
  severity?: string;
  action?: string;
  limit?: number;
  offset?: number;
  since?: string; // ISO timestamp for real-time polling
  user_only?: boolean; // If true, only return logs for current user (for regular users)
}): Promise<{ logs: AuditLog[]; total: number }> {
  const queryParams = new URLSearchParams();
  if (params?.severity) queryParams.append('severity', params.severity);
  if (params?.action) queryParams.append('action', params.action);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  if (params?.since) queryParams.append('since', params.since);
  if (params?.user_only) queryParams.append('user_only', 'true');

  const endpoint = `/audit/logs${queryParams.toString() ? `?${queryParams}` : ''}`;
  return adminFetch<{ logs: AuditLog[]; total: number }>(endpoint);
}

export async function getGlobalAuditLogs(params?: {
  severity?: string;
  action?: string;
  limit?: number;
  offset?: number;
  since?: string; // ISO timestamp for real-time polling
}): Promise<{ logs: AuditLog[]; total: number }> {
  const queryParams = new URLSearchParams();
  if (params?.severity) queryParams.append('severity', params.severity);
  if (params?.action) queryParams.append('action', params.action);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  if (params?.since) queryParams.append('since', params.since);

  const endpoint = `/audit/global${queryParams.toString() ? `?${queryParams}` : ''}`;
  return adminFetch<{ logs: AuditLog[]; total: number }>(endpoint);
}

/**
 * Get audit logs filtered by resource types
 * Used for settings page audit links (e.g., show only 'company' logs)
 */
export async function getAuditLogsByResourceTypes(params: {
  types: string[];
  limit?: number;
  offset?: number;
}): Promise<{ logs: AuditLog[]; total: number; hasMore: boolean }> {
  const queryParams = new URLSearchParams();
  queryParams.append('types', params.types.join(','));
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.offset) queryParams.append('offset', params.offset.toString());

  return adminFetch<{ logs: AuditLog[]; total: number; hasMore: boolean }>(
    `/audit/by-resource-types?${queryParams}`
  );
}

// SOC 2 COMPLIANCE: Audit log mutation functions removed
// Audit logs are immutable - use POST /logs/:id/correction for annotations

/**
 * Fetch sub-items (field changes and consequent actions) for a specific audit log entry
 * Used for lazy loading expandable row details
 */
export async function getAuditLogDetails(
  logId: string
): Promise<{ details: AuditLogSubItem[] }> {
  return adminFetch<{ details: AuditLogSubItem[] }>(`/audit/logs/${logId}/details`);
}

/**
 * Entity audit trail response with cursor-based pagination
 */
export interface EntityAuditResponse {
  logs: AuditLog[];
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
  limit: number;
}

/**
 * Fetch audit logs for a specific entity using cursor-based pagination
 * Follows industry best practices (AWS CloudTrail, Salesforce Field History)
 *
 * @param entityType - The type of entity (group, company, unit, user, site)
 * @param entityId - The UUID of the entity
 * @param params - Pagination params with cursor support
 */
export async function getEntityAuditLogs(
  entityType: 'company' | 'group' | 'unit' | 'user' | 'site',
  entityId: string,
  params?: {
    limit?: number;
    cursor?: string; // Opaque `${created_at}|${id}` token — pass back nextCursor verbatim, never hand-assemble (#636)
  }
): Promise<EntityAuditResponse> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.cursor) queryParams.append('cursor', params.cursor);

  const endpoint = `/audit/entity/${entityType}/${entityId}${queryParams.toString() ? `?${queryParams}` : ''}`;
  return adminFetch<EntityAuditResponse>(endpoint);
}

// =====================================================
// INVOICES
// =====================================================

export async function getInvoices(params?: {
  companyId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ invoices: Invoice[]; total: number }> {
  const queryParams = new URLSearchParams();
  if (params?.companyId) queryParams.append('companyId', params.companyId);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());

  const endpoint = `/invoices${queryParams.toString() ? `?${queryParams}` : ''}`;
  return adminFetch<{ invoices: Invoice[]; total: number }>(endpoint);
}

export async function getInvoiceDetails(id: string): Promise<{ invoice: Invoice }> {
  return adminFetch<{ invoice: Invoice }>(`/invoices/${id}`);
}

// =====================================================
// NOTIFICATIONS
// =====================================================

export interface AlertRule {
  key: string;
  label: string;
  description: string;
  conditionType: string | null;
  target: string | null;
  thresholdField: 'threshold' | 'minutes' | 'percent' | 'hours' | null;
  threshold: number | null;
  /** The critical battery rule's warning line, in percent (board only). */
  warningThreshold?: number;
  /** Critical battery: remind everyone while nobody owns it, every `reminderMinutes`. */
  reminderEnabled?: boolean;
  reminderMinutes?: number;
  /** Critical battery: alert again below `deeperThreshold` percent. */
  deeperEnabled?: boolean;
  deeperThreshold?: number;
  channels: string[];
  enabled: boolean;
  state: 'ok' | 'alerting';
  stateSince: string | null;
  lastFiredAt: string | null;
}

/** The fields of a rule a superadmin can change. */
export type AlertRulePatch = Partial<Pick<AlertRule,
  'enabled' | 'channels' | 'warningThreshold' | 'reminderEnabled' | 'reminderMinutes' | 'deeperEnabled' | 'deeperThreshold'
>> & { threshold?: number };

/** Systems/telematics alert rules. Seeded server-side; GET materialises them. */
export async function getAlertRules(): Promise<{ rules: AlertRule[] }> {
  return adminFetch<{ rules: AlertRule[] }>('/alerts');
}

export async function updateAlertRule(
  key: string,
  patch: AlertRulePatch,
): Promise<{ success: boolean; rule: AlertRule }> {
  return adminFetch<{ success: boolean; rule: AlertRule }>(`/alerts/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function getSystemNotifications(params?: {
  unread?: boolean;
  category?: string;
  limit?: number;
  offset?: number;
}): Promise<{ notifications: SystemNotification[]; total: number; unread_count: number }> {
  const queryParams = new URLSearchParams();
  if (params?.unread !== undefined) queryParams.append('unread', params.unread.toString());
  if (params?.category) queryParams.append('category', params.category);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());

  const endpoint = `/notifications${queryParams.toString() ? `?${queryParams}` : ''}`;
  return adminFetch<{ notifications: SystemNotification[]; total: number; unread_count: number }>(endpoint);
}

export async function markNotificationRead(id: string): Promise<{ success: boolean }> {
  return adminFetch<{ success: boolean }>(`/notifications/${id}/read`, {
    method: 'PATCH',
  });
}

export async function deleteNotification(id: string): Promise<{ success: boolean }> {
  return adminFetch<{ success: boolean }>(`/notifications/${id}`, {
    method: 'DELETE',
  });
}

/** User notification preference row (settings page). */
export interface NotificationSetting {
  id: string;
  title: string;
  description: string;
  email: boolean;
  push: boolean;
  sms: boolean;
}

export async function getNotificationSettings(): Promise<{ settings: NotificationSetting[] }> {
  await new Promise(resolve => setTimeout(resolve, 500));
  return {
    settings: [
      {
        id: 'security',
        title: 'Security Alerts',
        description: 'Login attempts, password changes, and security policies.',
        email: true,
        push: true,
        sms: true,
      },
      {
        id: 'billing',
        title: 'Billing & Invoices',
        description: 'Invoice generated, payment failed, subscription updates.',
        email: true,
        push: false,
        sms: false,
      },
      {
        id: 'system',
        title: 'System Updates',
        description: 'Maintenance windows, new features, and platform updates.',
        email: false,
        push: true,
        sms: false,
      },
      {
        id: 'units',
        title: 'Unit Status',
        description: 'Unit offline, low battery, geofence alerts.',
        email: true,
        push: true,
        sms: true,
      },
    ]
  };
}

export async function updateNotificationSettings(_settings: NotificationSetting[]): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, 800));
  // In a real app, this would PATCH the user's preferences
  return;
}

// =====================================================
// MESSAGES
// =====================================================

/** Message thread summary (messages settings page). */
export interface MessageThread {
  id: string;
  sender: string;
  avatar?: string;
  preview: string;
  time: string;
  unread: boolean;
}

/** Single message within a thread. */
export interface ThreadMessage {
  id: string;
  sender: 'me' | 'them';
  text: string;
  time: string;
}

export async function getMessageThreads(params?: {
  unread?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ threads: MessageThread[]; total: number }> {
  const queryParams = new URLSearchParams();
  if (params?.unread !== undefined) queryParams.append('unread', params.unread.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());

  const endpoint = `/messages/threads${queryParams.toString() ? `?${queryParams}` : ''}`;
  return adminFetch<{ threads: MessageThread[]; total: number }>(endpoint);
}

export async function getThreadMessages(threadId: string): Promise<{ thread: Record<string, unknown>; messages: ThreadMessage[] }> {
  return adminFetch<{ thread: Record<string, unknown>; messages: ThreadMessage[] }>(`/messages/threads/${threadId}`);
}

export async function sendMessage(threadId: string, text: string): Promise<{ success: boolean; message: Record<string, unknown> }> {
  return adminFetch<{ success: boolean; message: Record<string, unknown> }>(`/messages/threads/${threadId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

// =====================================================
// PROFILE
// =====================================================

export async function getProfileDetails(): Promise<{
  profile: {
    first_name: string;
    last_name: string;
    full_name: string;
    default_company_id: string | null;
    default_group_id: string | null;
    is_internal: boolean;
    current_avatar_id: string | null;
    phone?: string;
    job_title?: string;
    bio?: string;
    avatar_url?: string;
  }
}> {
  return adminFetch<{
    profile: {
      first_name: string;
      last_name: string;
      full_name: string;
      default_company_id: string | null;
      default_group_id: string | null;
      is_internal: boolean;
      current_avatar_id: string | null;
      phone?: string;
      job_title?: string;
      bio?: string;
      avatar_url?: string;
    }
  }>('/me/profile');
}

export async function updateProfile(data: {
  first_name?: string;
  last_name?: string;
  default_company_id?: string | null;
  default_group_id?: string | null;
  phone?: string;
  job_title?: string;
  bio?: string;
}): Promise<{ success: boolean }> {
  return adminFetch<{ success: boolean }>('/me/profile', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function changePassword(data: {
  current_password: string;
  new_password: string;
}): Promise<{ success: boolean }> {
  return adminFetch<{ success: boolean }>('/me/change-password', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function setup2FA(): Promise<{ qr_code: string; secret: string }> {
  return adminFetch<{ qr_code: string; secret: string }>('/me/setup-2fa', {
    method: 'POST',
  });
}

export async function revokeSessions(sessionIds: string[]): Promise<{ success: boolean }> {
  return adminFetch<{ success: boolean }>('/me/sessions', {
    method: 'DELETE',
    body: JSON.stringify({ session_ids: sessionIds }),
  });
}

// =====================================================
// MEDIA UPLOADS (Avatars & Logos)
// =====================================================

/**
 * Generic media upload helper
 * Uses FormData for file upload (not JSON)
 */
async function mediaUpload<T>(endpoint: string, file: File, fieldName: string): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  formData.append(fieldName, file);

  const response = await fetch(`${API_BASE_URL}/v1${endpoint}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'X-Supabase-Environment': currentSupabaseEnv,
      // Note: Don't set Content-Type - browser sets it with boundary for FormData
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || `Upload failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Generic media delete helper
 */
async function mediaDelete<T>(endpoint: string): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_BASE_URL}/v1${endpoint}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'X-Supabase-Environment': currentSupabaseEnv,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || `Delete failed: ${response.status}`);
  }

  return response.json();
}

// Avatar uploads
export interface AvatarUrls {
  id: string;
  small_webp_url: string;
  small_png_url: string;
  medium_webp_url: string;
  medium_png_url: string;
  large_webp_url: string;
  large_png_url: string;
  original_url: string;
}

export async function uploadAvatar(file: File): Promise<{ success: boolean; avatar: AvatarUrls }> {
  return mediaUpload<{ success: boolean; avatar: AvatarUrls }>('/me/avatar', file, 'avatar');
}

export async function deleteAvatar(): Promise<{ success: boolean }> {
  return mediaDelete<{ success: boolean }>('/me/avatar');
}

// Company logo uploads
export interface LogoUrls {
  id: string;
  small_webp_url: string | null;
  small_png_url: string | null;
  medium_webp_url: string | null;
  medium_png_url: string | null;
  large_webp_url: string | null;
  large_png_url: string | null;
  original_url: string;
  svg_url: string | null;
  is_svg: boolean;
}

export async function uploadCompanyLogo(companySlug: string, file: File): Promise<{ success: boolean; logo: LogoUrls }> {
  return mediaUpload<{ success: boolean; logo: LogoUrls }>(`/companies/${companySlug}/logo`, file, 'logo');
}

export async function deleteCompanyLogo(companySlug: string): Promise<{ success: boolean }> {
  return mediaDelete<{ success: boolean }>(`/companies/${companySlug}/logo`);
}

/** Valid logo variant types for upload/delete */
type LogoVariant = 'light' | 'dark' | 'icon_light' | 'icon_dark';

/**
 * Upload a theme-specific logo variant for a company
 * Stores the URL in the corresponding theme_json field
 */
export async function uploadCompanyLogoVariant(
  companySlug: string,
  file: File,
  variant: LogoVariant
): Promise<{ success: boolean; logo: LogoUrls }> {
  return mediaUpload<{ success: boolean; logo: LogoUrls }>(
    `/companies/${companySlug}/logo?variant=${variant}`,
    file,
    'logo'
  );
}

/**
 * Delete a theme-specific logo variant for a company
 * Removes the URL from the corresponding theme_json field
 */
export async function deleteCompanyLogoVariant(
  companySlug: string,
  variant: LogoVariant
): Promise<{ success: boolean }> {
  return mediaDelete<{ success: boolean }>(`/companies/${companySlug}/logo?variant=${variant}`);
}

// Group logo uploads
export async function uploadGroupLogo(groupId: string, file: File): Promise<{ success: boolean; logo: LogoUrls }> {
  return mediaUpload<{ success: boolean; logo: LogoUrls }>(`/groups/${groupId}/logo`, file, 'logo');
}

export async function deleteGroupLogo(groupId: string): Promise<{ success: boolean }> {
  return mediaDelete<{ success: boolean }>(`/groups/${groupId}/logo`);
}

// =====================================================
// INVITATIONS (Public - No Authentication Required)
// =====================================================

/**
 * Public fetch helper - no authentication required
 * Used for invitation acceptance flow before user is logged in
 */
async function publicFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}/v1/public${endpoint}`;

  console.log(`[publicApi] ${options.method || 'GET'} ${url}`);

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || error.error || `API Error: ${response.status}`);
  }

  return response.json();
}

export interface ValidateTokenResponse {
  valid: boolean;
  invitation: {
    email: string;
    company_name: string;
    groups: Array<{ group_id: string; group_name: string }>;
    role: string;
    inviter_name?: string;
    message?: string;
    expires_at: string;
    requires_password: boolean;  // true for HT staff roles
  };
}

/**
 * Validate an invitation token (public endpoint)
 * Call this when user lands on /accept-invite?token=xxx
 */
export async function validateInvitationToken(token: string): Promise<ValidateTokenResponse> {
  return publicFetch<ValidateTokenResponse>(`/invitations/validate?token=${encodeURIComponent(token)}`);
}

export interface VerifyCodeResponse {
  valid: boolean;
  attempts_remaining?: number;
  locked_until?: string;
}

/**
 * Verify the 4-digit verification code (public endpoint)
 * User must enter this code after clicking the email link
 */
export async function verifyInvitationCode(token: string, code: string): Promise<VerifyCodeResponse> {
  return publicFetch<VerifyCodeResponse>('/invitations/verify-code', {
    method: 'POST',
    body: JSON.stringify({ token, code }),
  });
}

export interface AcceptInvitationData {
  token: string;
  code: string;
  first_name: string;
  last_name: string;
  phone?: string;
  password?: string;  // Required for HT staff roles (admin, superadmin)
  profile_photo?: string;  // Base64 or URL
}

export interface AcceptInvitationResponse {
  success: boolean;
  user_id: string;
  email: string;
  requires_password: boolean;
  magic_link_sent?: boolean;  // true for regular users
  message: string;
}

/**
 * Accept an invitation and create user membership (public endpoint)
 * - For regular users: sends magic link for authentication
 * - For HT staff: requires password, can login immediately
 */
export async function acceptInvitation(data: AcceptInvitationData): Promise<AcceptInvitationResponse> {
  return publicFetch<AcceptInvitationResponse>('/invitations/accept', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// =====================================================
// SUPPORT
// =====================================================

export interface SupportTicketData {
  // User info
  name: string;
  email: string;
  phone?: string;

  // Ticket details
  category: string;
  categoryCode: string;
  priority: string;
  subject: string;
  message: string;

  // Unit info
  unitId?: string | null;
  unitCode?: string | null;

  // Troubleshooting info
  browser: string;
  page: string;
  companyId?: string | null;
  companyName?: string | null;
  companySlug?: string | null;
  accessLevel: string;

  // For ticket ID generation
  groupCode: string;
  siteCode: string;
  userInitials: string;

  // Attachments (base64 encoded)
  attachments?: Array<{
    name: string;
    type: string;
    size: number;
    data: string;
  }>;
}

export interface SupportTicketResponse {
  success: boolean;
  ticketId: string;
  message: string;
}

export async function submitSupportTicket(data: SupportTicketData): Promise<SupportTicketResponse> {
  return adminFetch<SupportTicketResponse>('/support/tickets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// =====================================================
// SECURITY MANAGEMENT (Superadmin only)
// =====================================================

// Types
export interface RateLimitConfig {
  id: string;
  endpoint_type: 'auth' | 'control' | 'query' | 'default';
  window_seconds: number;
  max_requests: number;
  enabled: boolean;
  updated_at: string;
}

export interface SuspiciousIP {
  id: string;
  ip_address: string;
  reason: string;
  hit_count: number;
  first_seen: string;
  last_seen: string;
  blocked: boolean;
  blocked_at: string | null;
  blocked_by: string | null;
  block_reason: string | null;
  block_expires_at: string | null;
  auto_blocked: boolean;
  metadata: Record<string, unknown>;
  location?: string;
}

export interface BlockedCountry {
  id: string;
  country_code: string;
  country_name: string;
  reason: string | null;
  blocked_at: string;
  blocked_by: string | null;
  expires_at: string | null;
}

export interface WhitelistEntry {
  id: string;
  ip_address: string;
  description: string;
  added_at: string;
  added_by: string | null;
}

// Anomaly detection rule configuration
export interface AnomalyRuleConfig {
  enabled: boolean;
  exemptRoles: string[];
  // Threshold values vary by rule type
  attempts?: number;       // failedLogins
  windowMinutes?: number;  // failedLogins, bulkDeletes
  count?: number;          // bulkDeletes
  windowHours?: number;    // geoAnomaly
  startHour?: number;      // offHours
  endHour?: number;        // offHours
}

// Full anomaly detection settings
export interface AnomalyDetectionSettings {
  failedLogins: AnomalyRuleConfig;
  bulkDeletes: AnomalyRuleConfig;
  geoAnomaly: AnomalyRuleConfig;
  offHours: AnomalyRuleConfig;
}

// Default anomaly detection settings (matches backend defaults)
export const DEFAULT_ANOMALY_SETTINGS: AnomalyDetectionSettings = {
  failedLogins: { enabled: true, attempts: 5, windowMinutes: 5, exemptRoles: [] },
  bulkDeletes: { enabled: true, count: 10, windowMinutes: 1, exemptRoles: [] },
  geoAnomaly: { enabled: true, windowHours: 1, exemptRoles: ['superadmin'] },
  offHours: { enabled: true, startHour: 6, endHour: 22, exemptRoles: ['superadmin'] },
};

export interface SecuritySettingsFull {
  security_settings?: AnomalyDetectionSettings;
  security_blocking?: {
    autoBlockEnabled: boolean;
    autoBlockThreshold: number;
    autoBlockDurationHours: number;
    blockResponseCode: number;
    blockResponseMessage: string;
  };
}

// Simplified type for auto-block settings panel
export interface SecuritySettings {
  autoBlockEnabled: boolean;
  autoBlockThreshold: number;
  autoBlockDurationHours: number;
  blockResponseCode: number;
  blockResponseMessage: string;
}

// Rate Limits
export async function getRateLimits(): Promise<RateLimitConfig[]> {
  const response = await adminFetch<{ rateLimits: RateLimitConfig[] }>('/security/rate-limits');
  return response.rateLimits;
}

export async function updateRateLimit(
  endpointType: string,
  config: { window_seconds?: number; max_requests?: number; enabled?: boolean }
): Promise<RateLimitConfig> {
  const response = await adminFetch<{ rateLimit: RateLimitConfig }>(`/security/rate-limits/${endpointType}`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
  return response.rateLimit;
}

// Suspicious IPs
export async function getSuspiciousIPs(params?: {
  status?: 'all' | 'blocked' | 'active';
  limit?: number;
  offset?: number;
}): Promise<SuspiciousIP[]> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.set('status', params.status);
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.offset) queryParams.set('offset', String(params.offset));

  const query = queryParams.toString();
  const response = await adminFetch<{ suspiciousIPs: SuspiciousIP[]; total: number }>(
    `/security/suspicious-ips${query ? `?${query}` : ''}`
  );
  return response.suspiciousIPs;
}

export async function blockIP(
  ip: string,
  data: { reason?: string; durationHours?: number }
): Promise<SuspiciousIP> {
  const response = await adminFetch<{ suspiciousIP: SuspiciousIP }>(`/security/suspicious-ips/${encodeURIComponent(ip)}/block`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.suspiciousIP;
}

export async function unblockIP(ip: string): Promise<SuspiciousIP> {
  const response = await adminFetch<{ suspiciousIP: SuspiciousIP }>(`/security/suspicious-ips/${encodeURIComponent(ip)}/block`, {
    method: 'DELETE',
  });
  return response.suspiciousIP;
}

// Country Blocking
export async function getBlockedCountries(): Promise<BlockedCountry[]> {
  const response = await adminFetch<{ blockedCountries: BlockedCountry[] }>('/security/blocked-countries');
  return response.blockedCountries;
}

export async function blockCountry(data: {
  countryCode: string;
  countryName: string;
  reason?: string;
  expiresAt?: string;
}): Promise<BlockedCountry> {
  const response = await adminFetch<{ blockedCountry: BlockedCountry }>('/security/blocked-countries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.blockedCountry;
}

export async function unblockCountry(countryCode: string): Promise<void> {
  await adminFetch<{ success: boolean }>(`/security/blocked-countries/${countryCode}`, {
    method: 'DELETE',
  });
}

// IP Whitelist
export async function getIPWhitelist(): Promise<WhitelistEntry[]> {
  const response = await adminFetch<{ whitelist: WhitelistEntry[] }>('/security/ip-whitelist');
  return response.whitelist;
}

export async function addToWhitelist(data: {
  ipAddress: string;
  description: string;
}): Promise<WhitelistEntry> {
  const response = await adminFetch<{ whitelistEntry: WhitelistEntry }>('/security/ip-whitelist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.whitelistEntry;
}

export async function removeFromWhitelist(id: string): Promise<void> {
  await adminFetch<{ success: boolean }>(`/security/ip-whitelist/${id}`, {
    method: 'DELETE',
  });
}

// Security Settings (Auto-blocking)
export async function getSecuritySettings(): Promise<SecuritySettings> {
  const response = await adminFetch<{ settings: SecuritySettingsFull }>('/security/settings');
  // Extract security_blocking settings with defaults
  const blocking = response.settings?.security_blocking;
  return {
    autoBlockEnabled: blocking?.autoBlockEnabled ?? true,
    autoBlockThreshold: blocking?.autoBlockThreshold ?? 3,
    autoBlockDurationHours: blocking?.autoBlockDurationHours ?? 24,
    blockResponseCode: blocking?.blockResponseCode ?? 403,
    blockResponseMessage: blocking?.blockResponseMessage ?? 'Access denied',
  };
}

export async function updateSecuritySettings(data: SecuritySettings): Promise<SecuritySettings> {
  await adminFetch<{ success: boolean }>('/security/settings', {
    method: 'PUT',
    body: JSON.stringify({
      securityBlocking: {
        autoBlockEnabled: data.autoBlockEnabled,
        autoBlockThreshold: data.autoBlockThreshold,
        autoBlockDurationHours: data.autoBlockDurationHours,
        blockResponseCode: data.blockResponseCode,
        blockResponseMessage: data.blockResponseMessage,
      },
    }),
  });
  return data;
}

// Anomaly Detection Settings
export async function getAnomalyDetectionSettings(): Promise<AnomalyDetectionSettings> {
  const response = await adminFetch<{ settings: SecuritySettingsFull }>('/security/settings');
  // Merge with defaults to ensure all fields are present
  const stored = response.settings?.security_settings;
  if (!stored) {
    return DEFAULT_ANOMALY_SETTINGS;
  }

  // Merge each rule with defaults
  return {
    failedLogins: { ...DEFAULT_ANOMALY_SETTINGS.failedLogins, ...stored.failedLogins },
    bulkDeletes: { ...DEFAULT_ANOMALY_SETTINGS.bulkDeletes, ...stored.bulkDeletes },
    geoAnomaly: { ...DEFAULT_ANOMALY_SETTINGS.geoAnomaly, ...stored.geoAnomaly },
    offHours: { ...DEFAULT_ANOMALY_SETTINGS.offHours, ...stored.offHours },
  };
}

export async function updateAnomalyDetectionSettings(
  data: AnomalyDetectionSettings
): Promise<AnomalyDetectionSettings> {
  await adminFetch<{ success: boolean }>('/security/settings', {
    method: 'PUT',
    body: JSON.stringify({
      securitySettings: data,
    }),
  });
  return data;
}
/**
 * Bulk-assign units to a production or one of its sites.
 *
 * The server validates the whole batch before writing anything, so a malformed
 * or unauthorized row rejects the request rather than half-applying it.
 */
export async function bulkAssignUnits(payload: {
  assignments: Array<{
    unitId: string;
    groupId: string;
    startDate: string | null;
    endDate: string | null;
  }>;
  setActive: boolean;
  /** APP-AI-2: which accepted AI suggestions this batch carries (audit only). */
  assist_session_id?: string;
  accepted_suggestion_ids?: string[];
}) {
  return adminFetch<{
    assigned: number;
    unchanged: number;
    failed: number;
    results: Array<{
      unitId: string;
      unitCode: string;
      outcome: 'assigned' | 'moved' | 'unchanged' | 'failed';
      fromGroupName: string | null;
      toGroupName: string;
      toGroupId: string;
      isSite: boolean;
      startDate: string | null;
      endDate: string | null;
      activatedFrom: string | null;
    }>;
    warnings: string[];
  }>('/units/bulk-assign', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Universal search P1: the read-only Callsigns directory (superadmin).
export type CallsignEntity = 'company' | 'production' | 'site' | 'model';
export interface CallsignDirectoryEntry {
  callsign: string;
  entity: CallsignEntity;
  id: string;
  name: string;
  company_name: string | null;
  active: boolean;
}

export async function getCallsignDirectory(): Promise<{ entries: CallsignDirectoryEntry[] }> {
  return adminFetch<{ entries: CallsignDirectoryEntry[] }>('/callsigns');
}
